/**
 * iter-261 — Stems Marketplace API client.
 *
 * Thin wrapper around the `/api/marketplace/stems/*` endpoints. All
 * requests include credentials so the Emergent Google Auth session
 * cookie is sent automatically. The backend treats anon viewers as
 * read-only (browse + view + download work without auth).
 */
const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/marketplace/stems`;

async function _parseOrThrow(res) {
  if (res.ok) return res.json();
  let detail = "";
  try {
    const body = await res.json();
    detail = body.detail || JSON.stringify(body);
  } catch (e) {
    void e;
    try { detail = await res.text(); } catch (e2) { void e2; }
  }
  const err = new Error(detail || `HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

/**
 * Browse the marketplace with optional filters.
 * @param {Object} filters
 * @param {string} [filters.q] full-text search across title/description/tags
 * @param {string} [filters.genre] one of the ALLOWED_GENRES values
 * @param {string} [filters.keyRoot] e.g. "C", "F#"
 * @param {string} [filters.keyQuality] "major" | "minor"
 * @param {number} [filters.tempoMin] BPM (≥ 20)
 * @param {number} [filters.tempoMax] BPM (≤ 300)
 * @param {string} [filters.sort] "recent" | "popular" | "downloads"
 * @param {number} [filters.limit]
 */
export async function browseStemPacks(filters = {}) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.genre) params.set("genre", filters.genre);
  if (filters.keyRoot) params.set("key_root", filters.keyRoot);
  if (filters.keyQuality) params.set("key_quality", filters.keyQuality);
  if (filters.tempoMin != null) params.set("tempo_min", String(filters.tempoMin));
  if (filters.tempoMax != null) params.set("tempo_max", String(filters.tempoMax));
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.limit) params.set("limit", String(filters.limit));
  const url = params.toString() ? `${BASE}/browse?${params.toString()}` : `${BASE}/browse`;
  const res = await fetch(url, { credentials: "include" });
  return _parseOrThrow(res);
}

export async function getStemPack(packId) {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}`, {
    credentials: "include",
  });
  return _parseOrThrow(res);
}

export async function publishStemPack(body) {
  const res = await fetch(`${BASE}/publish`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return _parseOrThrow(res);
}

export async function toggleStemPackLike(packId) {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}/like`, {
    method: "POST",
    credentials: "include",
  });
  return _parseOrThrow(res);
}

/**
 * iter-262 — Mark a pack as "remixed" (loaded into the studio's free
 * channels). Open to anyone — bumps `remix_count` and returns the
 * fresh pack so the caller can drive an optimistic UI update.
 */
export async function remixStemPack(packId) {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}/remix`, {
    method: "POST",
    credentials: "include",
  });
  return _parseOrThrow(res);
}

export async function deleteStemPack(packId) {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  return _parseOrThrow(res);
}

/** Owner-only: (re)generate the AI cover art. Resolves to the fresh pack. */
export async function regeneratePackCover(packId) {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}/cover`, {
    method: "POST",
    credentials: "include",
  });
  return _parseOrThrow(res);
}

/** Absolute cover URL for a pack (null when no cover has been generated). */
export function packCoverUrl(pack) {
  const u = pack?.cover_url;
  if (!u) return null;
  return u.startsWith("http") ? u : `${API}${u}`;
}

/**
 * Trigger a download of the pack — fetches the .zip blob and saves it
 * via a hidden <a download>. Returns the count from the
 * X-Stem-Pack-Count header so the caller can confirm-toast it.
 */
export async function downloadStemPack(packId, songName = "stems") {
  const res = await fetch(`${BASE}/pack/${encodeURIComponent(packId)}/download`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    await _parseOrThrow(res);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${songName || "stems"}-pack.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  const count = parseInt(res.headers.get("X-Stem-Pack-Count") || "0", 10) || 0;
  return { count };
}

// ── Allowed values (kept in sync with backend constants) ────────────────

export const ALLOWED_GENRES = [
  "hip-hop", "trap", "rnb", "pop", "rock", "edm", "house", "techno",
  "ambient", "lo-fi", "jazz", "soul", "funk", "country", "folk",
  "latin", "afrobeats", "k-pop", "metal", "punk", "indie", "classical",
  "world", "other",
];

export const SORT_OPTIONS = [
  { value: "recent", label: "Newest" },
  { value: "popular", label: "Most liked" },
  { value: "downloads", label: "Most downloaded" },
];

export const KEY_ROOTS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
